import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Save, X } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  title: "Título principal",
  subtitle: "Subtítulo / lema",
  paragraph: "Párrafo descriptivo",
  image_url: "Imagen principal",
  banner_url: "Imagen de banner / fondo",
};

/**
 * Slide-over inline editor for a single CMS section. Renders directly over the
 * current page (no navigation). Reuses /api/site-settings.
 */
export default function InlineSectionEditor({ open, onClose, sectionKey, sectionLabel, onSaved }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get("/site-settings")
      .then((r) => { if (!cancelled) setValues(r.data?.[sectionKey] || {}); })
      .catch(() => { if (!cancelled) setValues({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, sectionKey]);

  if (!open) return null;

  const setField = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const onUpload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/site-settings/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setField(field, fullUrl);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setUploading(null);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/site-settings", { [sectionKey]: values });
      toast.success("Sección actualizada");
      if (onSaved) {
        try { await onSaved(); } catch {}
      }
      onClose();
    } catch (e) {
      console.error("[InlineSectionEditor] save error:", e);
      toast.error(e.response?.data?.detail || e.message || "No se pudo guardar");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="inline-editor">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      {/* Slide-over panel */}
      <aside className="relative ml-auto w-full max-w-md h-full bg-card border-l border-border shadow-2xl flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-secondary">CMS · edición inline</p>
            <h2 className="font-display text-2xl text-primary mt-1">{sectionLabel || sectionKey}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full" data-testid="ie-close" aria-label="Cerrar editor">
            <X className="w-4 h-4" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <p className="text-sm text-foreground/60">Cargando…</p>
          ) : (
            <>
              {["title", "subtitle"].map((f) => (
                <div key={f}>
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{FIELD_LABELS[f]}</label>
                  <Input
                    className="mt-1"
                    value={values[f] || ""}
                    onChange={(e) => setField(f, e.target.value)}
                    data-testid={`ie-${f}`}
                  />
                </div>
              ))}
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{FIELD_LABELS.paragraph}</label>
                <Textarea
                  className="mt-1"
                  rows={4}
                  value={values.paragraph || ""}
                  onChange={(e) => setField("paragraph", e.target.value)}
                  data-testid="ie-paragraph"
                />
              </div>
              {["image_url", "banner_url"].map((f) => (
                <div key={f} className="rounded-xl border border-border p-3">
                  <label className="text-xs tracking-[0.2em] uppercase font-bold text-foreground/70">{FIELD_LABELS[f]}</label>
                  <Input
                    className="mt-2"
                    placeholder="URL https://… o pega aquí"
                    value={values[f] || ""}
                    onChange={(e) => setField(f, e.target.value)}
                    data-testid={`ie-${f}`}
                  />
                  <label className="mt-2 inline-flex items-center gap-2 px-3 h-9 rounded-full border border-border bg-card hover:bg-muted cursor-pointer text-xs font-semibold">
                    <Upload className="w-3.5 h-3.5" />
                    {uploading === f ? "Subiendo…" : "Subir archivo"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(f, e.target.files?.[0])} data-testid={`ie-${f}-file`} />
                  </label>
                  {values[f] && (
                    <img src={values[f]} alt="" className="mt-2 max-h-32 rounded-lg object-cover border border-border" />
                  )}
                </div>
              ))}
              <p className="text-xs text-foreground/60">Deja un campo vacío para conservar el contenido por defecto del diseño.</p>
            </>
          )}
        </div>

        <footer className="p-5 border-t border-border flex items-center gap-2">
          <Button onClick={save} disabled={busy} className="rounded-full" data-testid="ie-save">
            <Save className="w-4 h-4 mr-1" /> {busy ? "Guardando…" : "Guardar cambios"}
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
        </footer>
      </aside>
    </div>
  );
}
